/*
 * <testimonial-slider-autoplay>
 * Adds auto-play to the testimonial slider by driving the theme's existing
 * slider-component (it has no built-in autoplay). We click the native "next"
 * arrow on a timer, and scroll back to the start when the end is reached
 * (slider-component does not loop on its own).
 *
 * - Disabled when speed <= 0 or the user prefers reduced motion.
 * - Pauses on hover / focus, resumes when the pointer/focus leaves.
 * - Any direct user navigation (pointer down on an arrow or swipe on the
 *   track) stops auto-play for the session, so we never fight the visitor.
 */
if (!customElements.get('testimonial-slider-autoplay')) {
  customElements.define(
    'testimonial-slider-autoplay',
    class TestimonialSliderAutoplay extends HTMLElement {
      constructor() {
        super();
        this.timer = null;
        this.userStopped = false;
        this.onMouseEnter = this.pause.bind(this);
        this.onMouseLeave = this.resume.bind(this);
        this.onFocusIn = this.pause.bind(this);
        this.onFocusOut = this.resume.bind(this);
        this.onInteract = this.stop.bind(this);
      }

      connectedCallback() {
        this.speed = parseInt(this.dataset.speed, 10) || 0;
        this.slider = this.querySelector('[id^="Slider-"]');
        this.nextButton = this.querySelector('.slider-button--next');
        this.prevButton = this.querySelector('.slider-button--prev');

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.speed <= 0 || reduceMotion || !this.slider || !this.nextButton) return;

        // Temporary pause while the visitor is hovering or focused inside.
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);

        // Permanent stop once the visitor takes manual control.
        // pointerdown (not click) so our own programmatic .click() doesn't trigger it.
        this.nextButton.addEventListener('pointerdown', this.onInteract);
        if (this.prevButton) this.prevButton.addEventListener('pointerdown', this.onInteract);
        this.slider.addEventListener('pointerdown', this.onInteract);

        this.start();
      }

      disconnectedCallback() {
        this.pause();
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        if (this.nextButton) this.nextButton.removeEventListener('pointerdown', this.onInteract);
        if (this.prevButton) this.prevButton.removeEventListener('pointerdown', this.onInteract);
        if (this.slider) this.slider.removeEventListener('pointerdown', this.onInteract);
      }

      start() {
        if (this.timer || this.userStopped) return;
        this.timer = setInterval(this.advance.bind(this), this.speed * 1000);
      }

      pause() {
        clearInterval(this.timer);
        this.timer = null;
      }

      resume() {
        if (this.userStopped) return;
        this.start();
      }

      stop() {
        this.userStopped = true;
        this.pause();
      }

      advance() {
        // If there's another slide, advance; otherwise loop back to the first.
        if (this.nextButton && !this.nextButton.hasAttribute('disabled')) {
          this.nextButton.click();
        } else {
          this.slider.scrollTo({ left: 0, behavior: 'smooth' });
        }
      }
    }
  );
}
